<template>
  <div class="pl-page">
    <pro-list-frame
      v-if="isDesktopListLayout"
      ref="plTable"
      :title="$t('invoiceAndStockComparison')"
      icon="mdi-file-compare"
      :total="totalItems"
      :rows="items.length"
      :search.sync="search"
      :searchable="false"
      :columns="toggleableHeaders"
      :hidden="hiddenColumns"
      :chips="activeFilterChips"
      :filter-count="activeFilterChips.length"
      :selected-count="(selected || []).length"
      :loading="loading"
      :cell-sel="cellSel"
      :fixed="!mobileShow"
      @refresh="getItems"
      @print="printStudio = true"
      @filter="filter = true"
      @toggle-column="toggleColumn"
      @show-all-columns="hiddenColumns = []"
      @clear-chip="clearFilterChip"
      @clear-filters="resetFilters"
      @cell-down="onCellDown"
      @cell-over="onCellOver"
      @clear-cells="clearCellSel"
      @clear-selection="selected = []"
    >
      <v-data-table
        v-if="headers"
        ref="listTable"
        v-model="selected"
        :height="mobileShow ? 323 : plTableH"
        fixed-header
        :headers="tableHeaders"
        :items="items"
        :options.sync="tableOptions"
        :server-items-length="totalItems"
        :loading="false"
        :page.sync="page"
        multi-sort
        dense
        :show-select="showSelect"
        item-key="id"
        :footer-props="plFooterProps"
      >
        <template v-slot:no-data>
          <div class="pl-empty">
            <img src="@/assets/dashboard/no-data-cloud.png" alt="" />
            <p>{{ $t("no-data") }}</p>
          </div>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="pl-action-cell">
            <span class="pl-action-cell__row pl-num">{{
              faNum(parseInt(items.indexOf(item)) + parseInt(from))
            }}</span>
          </div>
        </template>
        <template v-slot:item.product.id="{ item }">
          <span class="pl-muted pl-num">{{
            item.product && item.product.id != null
              ? "#" + faNum(item.product.id)
              : $t("haveNot")
          }}</span>
        </template>
        <template v-slot:item.product.name="{ item }">
          <span class="pl-key">{{
            item.product && item.product.name ? item.product.name : $t("haveNot")
          }}</span>
        </template>
        <template v-slot:item.invoice.id="{ item }">
          <span class="pl-num">{{
            item.invoice && item.invoice.id != null
              ? "#" + faNum(item.invoice.id)
              : $t("haveNot")
          }}</span>
        </template>
        <template v-slot:item.invoice.date="{ item }">
          <span class="pl-num">{{
            item.invoice && item.invoice.date
              ? faNum(item.invoice.date)
              : $t("haveNot")
          }}</span>
        </template>
        <template v-slot:item.invoice_count="{ item }">
          <span class="pl-num">{{ faNum($numberFormat(item.invoice_count || 0)) }}</span>
        </template>
        <template v-slot:item.entrepot_count="{ item }">
          <span class="pl-num">{{ faNum($numberFormat(item.entrepot_count || 0)) }}</span>
        </template>
        <template v-slot:item.entrepot_ids="{ item }">
          <span class="pl-ellipsis">{{ formatEntrepotIds(item.entrepot_ids) }}</span>
        </template>
        <template v-slot:footer.page-text="{ pageStart, pageStop, itemsLength }">
          {{ faNum(pageStart) }}–{{ faNum(pageStop) }} {{ $t("of") }}
          {{ faNum($numberFormat(itemsLength)) }}
        </template>
        <template v-slot:footer.prepend>
          <div class="pl-goto">
            <span>{{ $t("goToPage") }}</span>
            <input
              v-model.number="page"
              type="number"
              min="1"
              :max="pageCount"
              class="pl-goto__input pl-num"
            />
            <span class="pl-num">/ {{ faNum(pageCount) }}</span>
          </div>
        </template>
      </v-data-table>
    </pro-list-frame>

    <pro-list-mobile
      v-else
      :title="$t('invoiceAndStockComparison')"
      :items="items"
      :total="totalItems"
      :loading="loading"
      :page="plPage"
      :page-count="pageCount || 1"
      :searchable="false"
      :filterable="true"
      :printable="true"
      :card="plAutoCard"
      :actions="plAutoActions"
      @load-more="plLoadPage"
      @refresh="plLoadPage(1)"
      @print="printStudio = true"
      @filter="filter = true"
    />

    <print-studio
      v-model="printStudio"
      report="list-invoiceAndStockComparison"
      :title="$t('invoiceAndStockComparison')"
      :columns="printColumns"
      :source="printSource"
      @error="serverErrorHandeler"
    />

    <v-dialog
      v-model="filter"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      max-width="960"
      scrollable
      content-class="acf-wrap"
    >
      <div class="acf acf--compact" :style="proListRootStyle" dir="rtl">
        <header class="acf-head">
          <span class="acf-head__icon">
            <v-icon size="18">mdi-filter-variant</v-icon>
          </span>
          <div class="acf-head__titles">
            <div class="acf-head__title">
              {{ $t("filter") }} {{ $t("invoiceAndStockComparison") }}
            </div>
            <div class="acf-head__sub">
              <template v-if="activeFilterChips.length">
                <span class="acf-badge">{{ faNum(activeFilterChips.length) }}</span>
                {{ $t("accFilterActive") }}
              </template>
              <template v-else>{{ $t("accFilterNone") }}</template>
            </div>
          </div>
          <button type="button" class="acf-x" :title="$t('close')" @click="filter = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>

        <div class="acf-body">
          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-account-outline</v-icon>
              {{ $t("sellFilterParties") }}
            </div>
            <div class="acf-grid acf-grid--3">
              <autocomplete
                v-model="account"
                :title="$t('customer')"
                type="person"
                :filter="true"
                :multiple="true"
              />
              <status v-model="sellStatus" :hideconfig="true" />
              <autocomplete
                v-model="currencyFilter"
                :title="$t('currency')"
                type="currency"
                :filter="true"
                :multiple="true"
              />
              <tag v-model="tag" />
              <autocomplete
                v-model="category"
                :title="$t('category')"
                type="accountCategory"
                :filter="true"
                :multiple="true"
              />
              <v-select
                v-model="payedStatus"
                :label="$t('payed-status')"
                :items="payedStatuses"
                dense
                outlined
                clearable
                hide-details
                item-value="value"
                item-text="label"
              />
            </div>
          </section>

          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-calendar-range</v-icon>
              {{ $t("accFilterDateTime") }}
            </div>
            <div class="acf-grid acf-grid--4">
              <div>
                <v-text-field
                  id="iscFromDate"
                  v-model="fromDate"
                  :label="$t('from') + ' ' + $t('date')"
                  prepend-inner-icon="mdi-calendar-start"
                  outlined
                  dense
                  clearable
                  hide-details
                />
                <date-picker
                  v-model="fromDate"
                  element="iscFromDate"
                  :locale="$store.state.date_locale"
                  :format="$store.state.date_format"
                  auto-submit
                  :color="
                    $vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light']
                      .primary
                  "
                />
              </div>
              <div>
                <v-text-field
                  id="iscToDate"
                  v-model="toDate"
                  :label="$t('until') + ' ' + $t('date')"
                  prepend-inner-icon="mdi-calendar-end"
                  outlined
                  dense
                  clearable
                  hide-details
                />
                <date-picker
                  v-model="toDate"
                  element="iscToDate"
                  :locale="$store.state.date_locale"
                  :format="$store.state.date_format"
                  :min="fromDate"
                  auto-submit
                  :color="
                    $vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light']
                      .primary
                  "
                />
              </div>
              <div>
                <v-text-field
                  id="iscFromTime"
                  v-model="fromTime"
                  :label="$t('from-hour')"
                  prepend-inner-icon="mdi-clock-start"
                  outlined
                  dense
                  clearable
                  readonly
                  hide-details
                />
                <date-picker
                  v-model="fromTime"
                  element="iscFromTime"
                  type="time"
                  :locale="$store.state.date_locale"
                  :format="'HH:mm'"
                  :display-format="'HH'"
                  auto-submit
                  :color="
                    $vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light']
                      .primary
                  "
                />
              </div>
              <div>
                <v-text-field
                  id="iscToTime"
                  v-model="toTime"
                  :label="$t('to-hour')"
                  prepend-inner-icon="mdi-clock-end"
                  outlined
                  dense
                  clearable
                  readonly
                  hide-details
                />
                <date-picker
                  v-model="toTime"
                  element="iscToTime"
                  type="time"
                  :locale="$store.state.date_locale"
                  :format="'HH:mm'"
                  :display-format="'HH'"
                  auto-submit
                  :color="
                    $vuetify.theme.themes[$vuetify.theme.isDark ? 'dark' : 'light']
                      .primary
                  "
                />
              </div>
            </div>
          </section>

          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-pound-box-outline</v-icon>
              {{ $t("accFilterNumberAmount") }}
            </div>
            <div class="acf-grid acf-grid--4">
              <v-text-field
                v-model="fromNumber"
                :label="$t('from') + ' ' + $t('number')"
                outlined
                dense
                clearable
                hide-details
              />
              <v-text-field
                v-model="toNumber"
                :label="$t('until') + ' ' + $t('number')"
                outlined
                dense
                clearable
                hide-details
              />
              <money
                v-model="fromAmount"
                :dense="true"
                :label="$t('from') + ' ' + $t('amount')"
                :outlined="true"
                :solo="false"
                :clearable="true"
                value-when-is-empty=""
                :options="options"
                class="dense"
              />
              <money
                v-model="toAmount"
                :dense="true"
                :label="$t('until') + ' ' + $t('amount')"
                :outlined="true"
                :solo="false"
                :clearable="true"
                value-when-is-empty=""
                :options="options"
                class="dense"
              />
            </div>
          </section>

          <section class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-office-building-outline</v-icon>
              {{ $t("sellFilterOrg") }}
            </div>
            <div class="acf-grid acf-grid--3">
              <autocomplete
                v-model="branch"
                title="branch"
                type="branch"
                :filter="true"
                :multiple="true"
              />
              <autocomplete
                v-model="entrepot"
                title="entrepot"
                type="entrepot"
                :filter="true"
                :multiple="true"
              />
              <autocomplete
                v-model="registerar"
                :title="$t('registerar')"
                type="partners"
                :multiple="true"
                :filter="true"
              />
              <v-select
                v-model="lockStatus"
                :label="$t('lockStatusDocuments')"
                :items="lockStatusItems"
                dense
                outlined
                clearable
                hide-details
                item-value="value"
                item-text="text"
              />
              <v-select
                v-model="selectedHeaders"
                :items="headers"
                :label="$t('sellFilterColumns')"
                dense
                outlined
                multiple
                return-object
                hide-details
              >
                <template v-slot:selection="{ item, index }">
                  <span v-if="index === 0" class="acf-sel">{{ item.text }}</span>
                  <span v-if="index === 1" class="acf-sel acf-sel--more">
                    +{{ faNum(selectedHeaders.length - 1) }}
                  </span>
                </template>
              </v-select>
            </div>
          </section>

          <section v-if="!isMobile && customFieldsPermission" class="acf-sec">
            <div class="acf-sec__head">
              <v-icon size="17">mdi-form-textbox</v-icon>
              {{ $t("sellFilterCustom") }}
            </div>
            <custom-fields
              v-model="customFields"
              :value="customFields"
              type-prop="invoiceCustomFields"
              :multiple="true"
            />
          </section>
        </div>

        <footer class="acf-foot">
          <button type="button" class="acf-btn acf-btn--primary" @click="applyFilter">
            <v-icon size="16">mdi-filter-check-outline</v-icon>
            {{ $t("accFilterApply") }}
          </button>
          <button
            type="button"
            class="acf-btn acf-btn--ghost"
            :disabled="!activeFilterChips.length"
            @click="resetFilters"
          >
            <v-icon size="16">mdi-filter-remove-outline</v-icon>
            {{ $t("accDocListClearFilters") }}
          </button>
          <span class="acf-spacer" />
          <button type="button" class="acf-btn" @click="filter = false">
            {{ $t("cancel") }}
          </button>
        </footer>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import General from "../../../../Mixins/general";
import ProList from "@/Mixins/proList";
import ProListFrame from "@/components/proList/ProListFrame.vue";
import ProListMobile from "@/components/proList/ProListMobile.vue";
import PrintStudio from "@/modules/print/PrintStudio.vue";

export default {
  name: "InvoiceAndStockComparison",
  components: {
    ProListFrame,
    ProListMobile,
    PrintStudio,
    autocomplete: () => import("../../components/general/patial/autocomplete"),
    Status: () => import("../../components/general/Status/status"),
    Tag: () => import("../../components/general/Tag/tag"),
    customFields: () => import("../../components/general/customFields/index.vue"),
  },
  mixins: [General, ProList],
  props: ["mobileShow"],
  data() {
    return {
      headers: null,
      items: [],
      selected: [],
      selectedHeaders: [],
      totalItems: 0,
      pageCount: 1,
      page: 1,
      from: 1,
      loading: true,
      filter: false,
      printStudio: false,
      search: null,
      invoiceIds: null,
      account: null,
      sellStatus: null,
      currencyFilter: null,
      fromAmount: null,
      fromNumber: null,
      fromDate: null,
      toAmount: null,
      toNumber: null,
      toDate: null,
      tag: null,
      category: null,
      branch: null,
      entrepot: null,
      lockStatus: null,
      registerar: null,
      payedStatus: null,
      fromTime: null,
      toTime: null,
      customFields: null,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      _skipOptionsWatch: false,
    };
  },
  computed: {
    showSelect() {
      return !this.isMobile;
    },
    showHeaders() {
      var app = this;
      return (this.headers || []).filter(function (s) {
        return app.selectedHeaders.includes(s);
      });
    },
    proListKey() {
      return "invoice-stock-comparison-columns";
    },
    proListLocked() {
      return ["actions"];
    },
    payedStatuses() {
      return [
        { label: this.$t("payed"), value: "payed" },
        { label: this.$t("not-payed"), value: "notPayed" },
      ];
    },
    activeFilterChips() {
      var chips = [];
      var push = function (key, label, value) {
        if (value === null || value === undefined || value === "") return;
        if (Array.isArray(value) && !value.length) return;
        chips.push({ key: key, label: label, value: value });
      };
      var labelOf = function (v) {
        if (v == null) return "";
        if (typeof v === "object") {
          return v.name || v.title || v.label || v.text || JSON.stringify(v);
        }
        return String(v);
      };
      var multi = function (arr) {
        if (!Array.isArray(arr) || !arr.length) return "";
        return arr
          .map(labelOf)
          .filter(Boolean)
          .join("، ");
      };
      push("account", this.$t("customer"), multi(this.account) || labelOf(this.account));
      push("sellStatus", this.$t("status"), multi(this.sellStatus) || labelOf(this.sellStatus));
      push(
        "currencyFilter",
        this.$t("currency"),
        multi(this.currencyFilter) || labelOf(this.currencyFilter)
      );
      push("fromAmount", this.$t("from") + " " + this.$t("amount"), this.fromAmount);
      push("toAmount", this.$t("until") + " " + this.$t("amount"), this.toAmount);
      push("fromNumber", this.$t("from") + " " + this.$t("number"), this.fromNumber);
      push("toNumber", this.$t("until") + " " + this.$t("number"), this.toNumber);
      push("fromDate", this.$t("from") + " " + this.$t("date"), this.fromDate);
      push("toDate", this.$t("until") + " " + this.$t("date"), this.toDate);
      push("tag", this.$t("tag"), multi(this.tag) || labelOf(this.tag));
      push("category", this.$t("category"), multi(this.category) || labelOf(this.category));
      push("branch", this.$t("branch"), multi(this.branch) || labelOf(this.branch));
      push("entrepot", this.$t("entrepot"), multi(this.entrepot) || labelOf(this.entrepot));
      push("lockStatus", this.$t("lockStatusDocuments"), labelOf(this.lockStatus));
      push(
        "registerar",
        this.$t("registerar"),
        multi(this.registerar) || labelOf(this.registerar)
      );
      push("payedStatus", this.$t("payed-status"), labelOf(this.payedStatus));
      push("fromTime", this.$t("from-hour"), this.fromTime);
      push("toTime", this.$t("to-hour"), this.toTime);
      push(
        "customFields",
        this.$t("customFields") || "customFields",
        multi(this.customFields) || labelOf(this.customFields)
      );
      return chips;
    },
    printColumns() {
      return this.plPrintColumns({
        "product.id": {
          width: 0.8,
          value: function (r) {
            return r.product && r.product.id != null ? "#" + r.product.id : "";
          },
        },
        "product.name": { width: 2 },
        "invoice.id": {
          width: 0.8,
          value: function (r) {
            return r.invoice && r.invoice.id != null ? "#" + r.invoice.id : "";
          },
        },
        "invoice.date": { width: 1 },
        invoice_count: { format: "number", width: 0.9 },
        entrepot_count: { format: "number", width: 0.9 },
        entrepot_ids: { width: 1.2 },
      });
    },
    printSource() {
      var app = this;
      return {
        currentRows: function () {
          return app.items || [];
        },
        total: function () {
          return app.totalItems || 0;
        },
        fetchPage: function (page, perPage) {
          var opts = Object.assign({}, app.tableOptions, {
            page: page,
            itemsPerPage: perPage,
          });
          return app.$axios
            .get(app.$endpoint + "reports/product-without-entrepot", {
              params: app.listParams(opts),
            })
            .then(function (res) {
              var d = res.data || {};
              var rows =
                d.data && Array.isArray(d.data.mismatches) ? d.data.mismatches : [];
              var meta = d.meta || {};
              return {
                rows: rows,
                lastPage: meta.last_page || 1,
                total: meta.total || rows.length,
              };
            });
        },
      };
    },
  },
  watch: {
    tableOptions: {
      handler() {
        if (this._skipOptionsWatch) return;
        this.getItems();
      },
      deep: true,
    },
  },
  beforeMount() {
    this.headers = [
      {
        text: this.$t("#"),
        align: "center",
        sortable: false,
        value: "actions",
        width: 42,
        class: "pl-action-col",
        cellClass: "pl-action-col",
      },
      {
        text: this.$t("product-id"),
        sortable: true,
        value: "product.id",
        width: 110,
      },
      {
        text: this.$t("product-name"),
        sortable: true,
        value: "product.name",
      },
      {
        text: this.$t("invoice-id"),
        sortable: true,
        value: "invoice.id",
        width: 110,
      },
      {
        text: this.$t("register-date-invoice"),
        sortable: true,
        value: "invoice.date",
        width: 120,
      },
      {
        text: this.$t("product-count-in-invoice"),
        sortable: true,
        value: "invoice_count",
        width: 120,
      },
      {
        text: this.$t("product-count-in-entrepot"),
        sortable: true,
        value: "entrepot_count",
        width: 120,
      },
      {
        text: this.$t("warehouse-remittances"),
        sortable: true,
        value: "entrepot_ids",
      },
    ];
    this.selectedHeaders = this.headers;
  },
  mounted() {
    this.getItems();
  },
  methods: {
    formatEntrepotIds(value) {
      if (value == null || value === "") return this.$t("haveNot");
      if (Array.isArray(value)) {
        return value.length ? value.join("، ") : this.$t("haveNot");
      }
      return String(value);
    },
    listParams(options) {
      var opts = options || this.tableOptions || {};
      return {
        type: "both",
        options: opts,
        invoice_ids: this.invoiceIds,
        account: this.account,
        status: this.sellStatus,
        currency: this.currencyFilter,
        fromAmount: this.fromAmount,
        fromNumber: this.fromNumber,
        fromDate: this.fromDate,
        toAmount: this.toAmount,
        toNumber: this.toNumber,
        toDate: this.toDate,
        tag: this.tag,
        category: this.category,
        branch: this.branch,
        entrepot: this.entrepot,
        lockStatus: this.lockStatus,
        registerar: this.registerar,
        payedStatus: this.payedStatus,
        fromTime: this.fromTime,
        toTime: this.toTime,
        customFields: this.customFields,
      };
    },
    applyFilter() {
      this.filter = false;
      if (this.tableOptions && this.tableOptions.page !== 1) {
        this.tableOptions = Object.assign({}, this.tableOptions, { page: 1 });
        return;
      }
      this.getItems();
    },
    clearFilterChip(key) {
      if (!key) return;
      if (Object.prototype.hasOwnProperty.call(this.$data, key)) {
        this[key] = null;
      }
      this.applyFilter();
    },
    resetFilters() {
      this.account = null;
      this.sellStatus = null;
      this.currencyFilter = null;
      this.fromAmount = null;
      this.fromNumber = null;
      this.fromDate = null;
      this.toAmount = null;
      this.toNumber = null;
      this.toDate = null;
      this.tag = null;
      this.category = null;
      this.branch = null;
      this.entrepot = null;
      this.lockStatus = null;
      this.registerar = null;
      this.payedStatus = null;
      this.fromTime = null;
      this.toTime = null;
      this.customFields = null;
      this.invoiceIds = null;
      this.applyFilter();
    },
    getItems() {
      var app = this;
      app.loading = true;
      var opts = app.tableOptions || { page: 1, itemsPerPage: 10 };
      app.$axios
        .get(app.$endpoint + "reports/product-without-entrepot", {
          params: app.listParams(opts),
        })
        .then(function (r) {
          var data = (r.data && r.data.data) || {};
          var mismatches = Array.isArray(data.mismatches) ? data.mismatches : [];
          var stamp = Date.now();
          mismatches.forEach(function (row, index) {
            row.id = index + "-" + stamp;
          });
          app.items = mismatches;
          var meta = (r.data && r.data.meta) || {};
          app.totalItems = meta.total || mismatches.length;
          app.pageCount = meta.last_page || 1;
          var page = opts.page || 1;
          var per = opts.itemsPerPage || 10;
          app.from = (page - 1) * per + 1;
          if (app.page !== page) {
            app._skipOptionsWatch = true;
            app.page = page;
            app.$nextTick(function () {
              app._skipOptionsWatch = false;
            });
          }
        })
        .catch(function (err) {
          app.serverErrorHandeler(err);
        })
        .finally(function () {
          app.loading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/sass/design-system/list/pro-list";
</style>
